import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleAuthMutation } from '../../../store/api/authApi';
import {
  authErrorMessage,
  GOOGLE_2FA_MESSAGE,
  GOOGLE_FAILED_MESSAGE,
} from '../../../store/api/authHelpers';
import { nextFromOAuthState, takeGoogleRemember } from '../../../utilities/googleAuth';
import { t } from '../../../i18n/i18n';
import './GoogleCallback.scss';

/**
 * Where Google drops the trader after the consent screen (`/auth/google`). It
 * sits outside both route guards: the session doesn't exist yet when this
 * mounts, and once the exchange lands, PublicRoute would bounce the user to the
 * dashboard before we could honour the `next` they were originally heading for.
 */
export default function GoogleCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [googleAuth] = useGoogleAuthMutation();
  const [error, setError] = useState(null);

  /**
   * The code is single-use, so the exchange must not run twice — which
   * StrictMode's double-mounted effects would otherwise do in development.
   */
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    const code = searchParams.get('code');
    if (!code) {
      // Google reports a refused or cancelled consent as `error` instead.
      setError(
        searchParams.get('error_description') ||
          (searchParams.get('error') ? 'Google sign-in was cancelled.' : GOOGLE_FAILED_MESSAGE),
      );
      return;
    }

    // The checkbox the trader ticked before leaving for Google, read once.
    googleAuth({ code, remember: takeGoogleRemember() })
      .unwrap()
      .then((data) => {
        if (data?.requires_2fa) {
          setError(GOOGLE_2FA_MESSAGE);
          return;
        }
        if (!data?.idToken) {
          setError(GOOGLE_FAILED_MESSAGE);
          return;
        }
        navigate(nextFromOAuthState(searchParams.get('state')) ?? '/dashboard', { replace: true });
      })
      .catch((err) => setError(authErrorMessage(err)));
    // Deliberately runs once, on the code present in the URL at mount — the
    // `exchanged` ref above is what actually enforces that.
  }, [googleAuth, navigate, searchParams]);

  return (
    <div className="google-callback">
      {error ? (
        <div className="google-callback__panel" role="alert">
          <h1 className="google-callback__title">{t('Sign-in failed')}</h1>
          <p className="google-callback__message">{t(error)}</p>
          <Link to="/sign-in" className="google-callback__link">
            {t('Back to sign in')}
          </Link>
        </div>
      ) : (
        <div className="google-callback__panel">
          <span className="google-callback__spinner" aria-hidden="true" />
          <p className="google-callback__message">{t('Verifying your Google account…')}</p>
        </div>
      )}
    </div>
  );
}
